import { forwardRef } from 'react';

import type { Tone } from './types';

export interface MetricTileProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  tone?: Tone;
  trailingSlot?: React.ReactNode;
  description?: React.ReactNode;
}

const toneBorderClasses: Record<Tone, string> = {
  neutral: 'border-l-[color:var(--color-severity-info-solid)]',
  success: 'border-l-[color:var(--color-status-resolved-solid)]',
  warning: 'border-l-[color:var(--color-sla-at-risk-solid)]',
  danger: 'border-l-[color:var(--color-sla-breached-solid)]',
  info: 'border-l-[color:var(--color-status-new-solid)]',
  critical: 'border-l-[color:var(--color-severity-critical-solid)]',
  'severity-high': 'border-l-[color:var(--color-severity-high-solid)]',
  'severity-medium': 'border-l-[color:var(--color-severity-medium-solid)]',
  'severity-low': 'border-l-[color:var(--color-severity-low-solid)]',
};

function cn(...parts: Array<string | undefined | false | null>): string {
  return parts.filter(Boolean).join(' ');
}

function getAccessibleMetricLabel(label: React.ReactNode, value: React.ReactNode): string | undefined {
  if (typeof label !== 'string') {
    return undefined;
  }

  if (typeof value !== 'string' && typeof value !== 'number') {
    return undefined;
  }

  return `${label}: ${value}`;
}

export const MetricTile = forwardRef<HTMLDivElement, MetricTileProps>(function MetricTile(
  { label, value, tone = 'neutral', trailingSlot, description, className, ...rest },
  ref,
) {
  const defaultAriaLabel = getAccessibleMetricLabel(label, value);

  return (
    <div
      ref={ref}
      role="group"
      className={cn(
        'rounded-xl border-l-4 bg-[color:var(--color-surface)] p-5 shadow-card',
        toneBorderClasses[tone],
        className,
      )}
      aria-label={rest['aria-label'] ?? defaultAriaLabel}
      {...rest}
    >
      <div className="text-xs font-medium uppercase tracking-wide text-[color:var(--color-text-muted)]">{label}</div>
      <div className="mt-2 flex items-start justify-between gap-4">
        <div className="text-3xl font-semibold tabular-nums text-[color:var(--color-text-primary)]">{value}</div>
        {trailingSlot ? <div className="shrink-0">{trailingSlot}</div> : null}
      </div>
      {description ? <div className="mt-2 text-sm text-[color:var(--color-text-secondary)]">{description}</div> : null}
    </div>
  );
});
